import React, {useContext, useMemo, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {useForm} from "react-hook-form";
import Form, {ActionButton, Actions, BasicInput} from "view/components/Form";
import FlexBox from "view/components/Layout/FlexBox";
import NewTabIcon from "view/images/NewTabIcon";
import {Typography} from "@material-ui/core";
import {
  IdealBankElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  CartFlow,
  CartFlowType,
} from "view/apps/Exhibitor/pages/Cart/components/Payment/types";

import {PlaceOrderCallback} from "view/apps/Exhibitor/pages/Cart/components/types";
import {CurrencyContext} from "view/context/CurrencyContext";
import {
  Currency,
  IExhibitorPair,
  IOrganiser,
  IShow,
} from "@evennt-tech/api-v2-core";
import stripeService from "api/stripe";
import {IdealPayment, PaymentMethodType} from "ts/api/paymentMethod";
import {useSelector} from "react-redux";
import {selectTotalAmount} from "store/exhibitors/cartStepper/slice";
import Transfer from "ts/api/transfer";
import {generatePath} from "react-router-dom";
import {SHOW_EXHIBITOR_ORDER_DETAILS_PATH} from "view/routes";
import getTransfers from "tools/order/getTransfers";
import orderService from "api/order";
import {removeItem} from "tools/localStorage";
import getCountryByCurrency from "view/apps/Exhibitor/pages/Cart/helpers/getCountryByCurrency";
import formatPrice from "tools/formats/formatPrice";
import Alert from "view/components/Alert";
import {useIdealOptions} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/IDEAL/helpers/useIdealOptions";
import clsx from "clsx";

import {generateTestId} from "tools/cypress/generateTestId";
import {
  useIsShowDiscountCodesInvalid,
  useShowDiscountCodesActions,
} from "store/discount-code/discount-code-store";
import {getUniqueProductCategoryIdsBySupplier} from "tools/product/getUniqueProductCategoryIdsBySupplier";
import {useQuickOrderId} from "hooks/show/order";
import orderRefs from "api/shows/orders/refs";
import {IdealOrder} from "ts/api/order";
import {quickOrderIdParamName} from "tools/routing";
import {getTruncatedString} from "tools/common/getTruncatedString";
import {format} from "date-fns";
import {getReadableString} from "constraints/general";
import useStyles from "./styles";

type IdealDetails = {fullName: string};

type Props = CartFlow & {
  show: IShow;
  exhibitorPair: IExhibitorPair;
  orderId?: string;
  organiser: IOrganiser;
  orderDescription: string;
  transferredPaymentDescription: string;
  onPlaceOrder: PlaceOrderCallback;
};

const Ideal = (props: Props) => {
  const {
    orderId: propsOrderId,
    show,
    exhibitorPair,
    orderDescription: description,
    onPlaceOrder,
    type: cartFlowType,
    organiser,
    transferredPaymentDescription,
    products,
  } = props;
  const quickOrderId = useQuickOrderId();
  const classes = useStyles();
  const {t} = useTranslate();

  const stripe = useStripe();
  const elements = useElements();
  const currency = useContext(CurrencyContext);
  const totalAmount = useSelector(selectTotalAmount);
  const showId = show.id;

  const {validate: validateDiscountCodes} = useShowDiscountCodesActions();
  const isDiscountCodesInvalid = useIsShowDiscountCodesInvalid(showId);

  const isLocalCartFlow = cartFlowType === CartFlowType.local;

  const [isIdealReady, setIsIdealReady] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isBankSelected, setIsBankSelected] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);

  const idealOptions = useIdealOptions();
  const categoryIdsBySupplier = useMemo(
    () => getUniqueProductCategoryIdsBySupplier(products),
    [products]
  );

  const form = useForm<IdealDetails>({
    defaultValues: {
      fullName: "",
    },
  });
  const {handleSubmit} = form;

  const generateRedirectUrl = (orderId: string, groupId?: string | null) => {
    const baseUrl = `${window.location.origin}${generatePath(
      SHOW_EXHIBITOR_ORDER_DETAILS_PATH,
      {
        showId,
        orderId,
        exhibitorPairId: exhibitorPair.id,
      }
    )}`;
    const url = new URL(baseUrl);
    if (groupId) {
      url.searchParams.set("orderGroupId", groupId);
    }
    if (quickOrderId) {
      url.searchParams.set(quickOrderIdParamName, quickOrderId);
    }
    return url.toString();
  };

  const handleMakePayment = async ({
    orderId,
    fullName,
    groupId,
    transfers,
    order,
  }: {
    orderId: string;
    transfers: Transfer[];
    fullName: string;
    groupId?: string | null;
    order?: IdealOrder;
  }) => {
    const shouldQuit = currency !== Currency.eur || !stripe || !elements;
    if (shouldQuit) {
      return;
    }

    if (order && quickOrderId) {
      await orderRefs.getCollection({showId: show.id}).doc(orderId).set(order);
    }

    const idealBankElement = elements.getElement(IdealBankElement);

    if (!idealBankElement) {
      throw new Error("iDEAL bank element is not ready");
    }

    const {
      clientSecret,
      paymentIntentId,
    } = await stripeService.makePayment<IdealPayment>({
      amount: totalAmount.withoutApproval,
      currency: Currency.eur,
      country: getCountryByCurrency(Currency.eur),
      showId: show.id,
      description,
      orderId,
      type: PaymentMethodType.idealPayment,
      showName: getTruncatedString(show.name, 50),
      showStartDate: format(
        new Date(show.liveStart),
        `${getReadableString(show)}, HH:mm`
      ),
    });
    await orderService.update(showId, orderId, {paymentIntentId});

    const redirectUrl = generateRedirectUrl(orderId, groupId);

    const {error} = await stripe.confirmIdealPayment(clientSecret, {
      payment_method: {
        ideal: idealBankElement,
        billing_details: {name: fullName.trim()},
      },
      return_url: redirectUrl,
    });

    if (error?.message) {
      await orderService.deleteItem(show.id, orderId);
      console.error("Payment method wasn't created");
      throw new Error(error.message);
    }
  };

  const onSubmit = async ({fullName}: IdealDetails) => {
    if (!isBankSelected || !isIdealReady) {
      return;
    }

    const isDiscountCodeValid = await validateDiscountCodes(
      showId,
      exhibitorPair.standId,
      categoryIdsBySupplier
    );
    if (!isDiscountCodeValid) {
      return;
    }
    let storageKey = "";
    try {
      if (error) {
        setError("");
      }
      const placeOrderResult = isLocalCartFlow
        ? await onPlaceOrder({isDraft: !!quickOrderId})
        : null;
      if (isLocalCartFlow) {
        if (!placeOrderResult) {
          throw new Error("Order(s) didn't create");
        }
        const {
          orderWithoutApproval,
          ordersWithApproval,
          transfersWithoutApproval,
        } = placeOrderResult;
        const idWithoutApproval = orderWithoutApproval?.id;
        const idsWithApproval = ordersWithApproval?.map(({id}) => id);

        const orderId = idWithoutApproval || idsWithApproval[0];

        if (!orderId) {
          throw new Error("Order(s) didn't create");
        }
        if (idWithoutApproval && transfersWithoutApproval) {
          await handleMakePayment({
            orderId: idWithoutApproval,
            transfers: transfersWithoutApproval,
            fullName,
            groupId: placeOrderResult.groupId,
            order: (orderWithoutApproval ||
              ordersWithApproval[0]) as IdealOrder,
          });
        }
      } else if (cartFlowType === CartFlowType.created && propsOrderId) {
        const transfers = await getTransfers({
          products,
          organiser,
          show,
          transferredPaymentDescription,
          orderId: propsOrderId,
        });
        await orderService.proceedToPayment(show.id, propsOrderId, {
          paymentType: PaymentMethodType.idealPayment,
          transfers,
        });

        await handleMakePayment({orderId: propsOrderId, transfers, fullName});
      }
      await orderService.markOrderProductImagesUsed(products);
    } catch (e) {
      console.error("Error occurred performing payment: ", e);
      if (storageKey) {
        removeItem(storageKey);
      }
      if (e instanceof Error) {
        setError(e.message);
      }
    }
  };

  return (
    <Form
      className={classes.form}
      onSubmit={handleSubmit(onSubmit)}
      form={form}
    >
      <BasicInput
        name="fullName"
        label={t("exhibitor.cart.ideal.full_name")}
        required
        rules={{validate: (val: string) => !!val.trim()?.length}}
        style={{margin: 0}}
      />
      <IdealBankElement
        options={idealOptions}
        onReady={() => setIsIdealReady(true)}
        onChange={() => {
          if (!isBankSelected) {
            setIsBankSelected(true);
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={clsx(classes.idealElement, {[classes.focused]: isFocused})}
      />
      {!!error && <Alert severity="error">{error}</Alert>}
      <FlexBox alignCenter gap={1}>
        <NewTabIcon />
        <Typography variant="h3" className={classes.infoText}>
          <T keyName="exhibitor.cart.ideal.redirect_info" />
        </Typography>
      </FlexBox>
      <Actions
        isLeftAction={cartFlowType === CartFlowType.local}
        className={classes.actions}
      >
        {isLocalCartFlow && (
          <ActionButton
            {...generateTestId("exhibitor-cart-change-billing-details-btn")}
            text={t("exhibitor.cart.ideal.change_billing_details")}
            type="button"
            variant="text"
            className={classes.leftAction}
          />
        )}
        <ActionButton
          {...generateTestId("exhibitor-cart-pay-btn")}
          text={t("exhibitor.cart.payment.pay_amount", {
            amount: formatPrice(totalAmount.withoutApproval, currency),
          })}
          variant="contained"
          type="submit"
          disabled={!isIdealReady || !isBankSelected || isDiscountCodesInvalid}
        />
      </Actions>
    </Form>
  );
};

export default Ideal;
